import { assetIdFromUrl } from '~/common/helpers/asset_id_from_url'
import { Controller } from 'stimulus'
import { Editor, getMarkRange } from '@tiptap/core'
import { debounce } from 'lodash-es'
import { useIntersection } from 'stimulus-use'
import $ from 'jquery'
import * as marked from 'marked'
import TurndownService from 'turndown'
import { gfm } from '@guyplusplus/turndown-plugin-gfm'

import {
  updateToolbarButtonsActiveState,
  fireSetDecorationFromEditorEvent,
  fireSetHighlightColorEvent,
  fireSetTextColorEvent,
  collectTiptapEditorEventTypes,
  matchingTiptapEditorMethod,
  stimulusParamsFromElement,
  applySetContent,
  isBareUrl,
  pastedUrlAction,
  markdownToHtml,
  markdownToHtmlOrText,
  loadWebfont,
  fetchDefaultFontSize,
} from './utilities'
import { ToolbarActions } from './toolbar_actions'
import { configuredExtensions } from './extensions'

import './editor_component.scss'
import './bot_object_pill.css'
import { addBotObjectRule } from './bot_object_turndown_rule'
import { addTightListRules } from './tight_list_turndown_rules'
import { isLastAddedNode } from '../builder/editor/helpers'
import { onViewportChange } from '../builder/editor/shared_observers'
import { LocalDraftManager } from '~/common/extensions/local_draft_manager'
import { promptedPersonalizationSnippet } from '~/admin/personalizations/prompted_snippet'
import { createEmbed, embedAttachState } from '~/common/helpers/embed'
import { createIdleTaskQueue } from '~/lib/idle_task_queue'

// Shared across every editor on the page: a content-heavy page (e.g. a long landing page with
// many WYSIWYG nodes) can have dozens of editors become eligible to initialize in the same burst
// (see `appear` below) — this queue spreads their construction across separate idle turns instead
// of running them all back-to-back in one frame, which is what actually freezes the tab. ENG-33781.
const lazyInitQueue = createIdleTaskQueue()

// A `pointerover` that a scroll produced under a cursor nobody moved is not the user reaching for
// the node. Chrome re-runs hover after a scroll, so one fires for every node that passes under the
// cursor — which is where a wheel or trackpad user's cursor sits. Measured on a 313-text-node page,
// scrolling it end to end that way: 56 editors built, 22 with this, and 1 once the hover's own wait
// is cancelled on `pointerleave` too (see `armInteractionMount`), because such a node is one the
// cursor then leaves.
//
// A tenth of a second, because a device that is really scrolling fires `scroll` every frame; what
// gets through is a scroll that paused, which is fair to read as "stopped here".
const SCROLL_HOVER_GRACE_MS = 100
const lastScrollAt = new WeakMap()

// Subscribed when a node arms, not lazily on the first hover: a scroll that happens before anyone
// has hovered is exactly the one this exists to catch, and registering on first read would miss it.
// One subscriber per document, on the builder's own shared channel rather than a second
// document-level scroll listener beside it.
function watchScrolling(doc) {
  if (lastScrollAt.has(doc)) return

  // Not 0: `performance.now()` starts near zero, so "scrolled at 0" would swallow every hover in
  // the document's first SCROLL_HOVER_GRACE_MS.
  lastScrollAt.set(doc, Number.NEGATIVE_INFINITY)
  onViewportChange(doc, () => lastScrollAt.set(doc, performance.now()))
}

function scrolledJustNow(element) {
  const at = lastScrollAt.get(element.ownerDocument)
  return at !== undefined && performance.now() - at < SCROLL_HOVER_GRACE_MS
}

// How long a hover waits before building the editor. Building it inserts DOM into the canvas, and
// any DOM insert there costs a fixed ~580ms of style recalc whatever its size (see the hover
// latency doc) — which lands on the hover the user is in the middle of. Measured, cursor arriving
// to the node's hover state on screen, over six nodes: **727ms** when the editor is built in the
// hover's own frame, **204ms** when it waits this long, against **210ms** for a node already built
// and **110ms** for one with no editor to build at all. A frame is not enough (rAF callbacks run
// before the paint, and one made no difference at all); the recalc has to miss the whole rendering
// opportunity the hover belongs to. A press inside the wait is covered by the pointerdown trigger,
// which mounts on the spot.
const MOUNT_AFTER_HOVER_MS = 200

const EMBEDDABLE_URL_PATTERNS = [
  /(?:\byoutube(?:-nocookie)?\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|\byoutu\.be\/|\byoutube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  /\bvimeo\.com\/\d+/,
  /\bplayer\.vimeo\.com\/video\/\d+/,
  /\bloom\.com\/share\/([a-zA-Z0-9]+)/,
  /\brumble\.com\/([a-zA-Z0-9]+)/,
  // Keep in sync with Embed::SPOTIFY_REGEX (app/models/embed.rb), including the intl-xx/ prefix.
  /\bopen\.spotify\.com\/(?:intl-[a-z-]+\/)?(?:track|album|playlist|episode|show|artist)\/[a-zA-Z0-9]+/,
]

export default class extends Controller {
  // The toolbar's own controls (colors, decoration, font family/size) are not listed: they end
  // up in the portaled toolbar, out of Stimulus's reach. See toolbarControl.
  static targets = [
    'input',
    'wysiwyg',
    'placeholderContent',
    'placeholderHint',
    'toolbar',
    'toolbarTemplate',
    'source',
  ]

  static values = {
    forBulletList: Boolean,
    forHeading: Boolean,
    listIcon: String,
    placeholder: String,
    formSubmitShortcut: Boolean,
    debounce: { type: Number, default: 50 },
    mentionsUrl: String,
    headingTag: String,
    linkPicker: Boolean,
    unsetAlignment: Boolean,
    markdown: Boolean,
    autofocus: Boolean,
    fontFamily: String,
    fontSize: String,
    fontSizeUnit: String,
    personalizations: Array,
    // Merge tags the link dialog offers as URL targets, grouped server-side so this editor and
    // the classic TinyMCE field cannot drift into offering different ones.
    linkPickerGroupedResources: Array,
    // Translated labels for the link info box on member sites.
    linkBoxLabels: Object,
    interpolationStyle: String,
    chatBotSkills: Array,
    botObjects: Boolean,
    botObjectPickerUrl: String,
    sourceCodeEnabled: Boolean,
    mediaType: String,
    withImage: Boolean,
    withIconInsert: Boolean,
    embeddedVideo: Boolean,
    toolbarContentUrl: String,
    eager: Boolean,
    mountOnInteraction: Boolean,
  }

  // Editors initialize lazily when they come within half a viewport of the visible area:
  // useIntersection observes on connect, and the IntersectionObserver delivers an initial callback
  // with the current state — so `appear` also fires right away for editors already in range. Two
  // browser quirks force the exact options here: with the implicit root, rootMargin is ignored
  // inside iframes (where the builder renders every editor), so the root must be the explicit
  // document; and with a document root, percentage margins resolve against the document's whole
  // scroll area, so the margin must be in pixels. Kept to half a viewport (rather than a full one)
  // so a content-heavy page doesn't make every editor within ~3 viewports eligible at once — see
  // `lazyInitQueue` below, which handles the resulting burst either way. ENG-33781.
  //
  // `eager` opts an editor out of all of that and mounts it here in connect(), for an editor the
  // user has already committed to using — a form in a modal they opened by clicking "Edit".
  //
  // What makes lazy init wrong there is not the observer: for an AJAX modal it reports the editor
  // visible almost immediately (~23ms), because el-transition's `enter` drops the `hidden` class at
  // the *start* of the show animation. It's the two chained idle callbacks — the one below and
  // another inside lazyInitQueue — each with a 500ms timeout. On a busy main thread neither gets an
  // idle window, so both run to their timeouts. Measured on the forum edit modal (ENG-31025) with
  // idle callbacks forced to their deadline: ProseMirror mounted 1088ms after the modal markup
  // landed, 761ms after the modal had finished animating in — a modal sitting there with a title
  // and no body. With `eager` it mounts in the same tick as the markup. (Before ENG-33781 added
  // lazyInitQueue there was only one 500ms hop, which is exactly the "half a second later the
  // content editing will pop in" in the original report; it has since doubled.)
  //
  // Done here rather than by dispatching `editor:initialize` at the container from the modal or
  // form controller: that event is wired as a Stimulus data-action, so it is only heard once THIS
  // controller has connected, and every controller is its own dynamically-imported chunk (see
  // controllers/loader/lazy_loader.ts). The tiptap chunk is one of the largest in the app, so on a
  // slow client the dispatch lands before there is any listener and silently does nothing —
  // exactly where the bug reproduces. connect() cannot have that problem: it *is* the controller
  // being ready, so this is the earliest moment the editor can possibly exist.
  connect() {
    if (this.eagerValue) {
      this.initializeEditor()
      return
    }

    if (this.mountOnInteractionValue) {
      this.armInteractionMount()
      return
    }

    const viewportHeight = this.element.ownerDocument.defaultView.innerHeight
    useIntersection(this, {
      root: this.element.ownerDocument,
      rootMargin: `${Math.round(viewportHeight / 2)}px`,
    })
  }

  // `mount_on_interaction` replaces "is it near the viewport?" with "is the user reaching for it?".
  // For a surface that renders hundreds of editors at once — the page builder renders one per text
  // node — scrolling past a paragraph says nothing about wanting to edit it, and building an editor
  // for every one the user scrolled by costs ~2,980 DOM nodes and ~70ms of main thread each.
  //
  // `pointerover` is the trigger rather than `pointerdown` because it fires FIRST for every input
  // type — a mouse arriving over the node, and the pointerover/pointerdown pair a finger or pen
  // emits on touch — so the editor is usually already there, and its box already contenteditable,
  // by the time the browser turns a press into a caret, a drag-selection, or (via SortableJS's
  // `isContentEditable` bail) a node drag. Usually, not always, because that mount waits — see
  // MOUNT_AFTER_HOVER_MS and SCROLL_HOVER_GRACE_MS for the two pointerovers that are not the user
  // reaching for anything.
  //
  // `pointerdown` is what makes the wait safe, and covers what pointerover cannot: a press inside
  // it, and a node re-rendering under a cursor that never moves afterwards, which fires no enter
  // event at all. It places the caret from the press coordinates itself, since the element the
  // browser hit-tested is gone by the time it would have done that. Verified in a browser that a
  // press taking this path still places the caret, still selects text by dragging, and still does
  // not drag the node.
  //
  // `focusin` covers Tab, and anything programmatic (editor:setContent, editor:focus, the settings
  // pane's refocus) goes through ensureEditor() as before, which mounts on the spot.
  armInteractionMount() {
    // A node the user just inserted has to mount now: TipTap puts the caret in it through the
    // `autofocus` constructor option, and nobody is going to hover a node that appeared for them.
    // So does one whose stored HTML the editor still owes a rewrite to.
    if (isLastAddedNode(this.element) || this.contentDisagreesWithSettings()) {
      this.initializeEditor()
      return
    }

    // Static HTML is not focusable, so without this a keyboard user could not reach the text at
    // all — ProseMirror's contenteditable box is what normally carries the node's tab stop. Set
    // here rather than server-side so it lasts exactly as long as the wait does: disarming takes
    // it away again, and the mounted editor is focusable on its own.
    this.interactionFocusHost?.setAttribute('tabindex', '0')

    watchScrolling(this.element.ownerDocument)

    this._interactionMountAbort = new AbortController()
    const { signal } = this._interactionMountAbort
    const on = (type, handler) =>
      this.element.addEventListener(type, handler, { signal })

    let booked = null
    signal.addEventListener('abort', () => clearTimeout(booked))

    on('pointerover', () => {
      if (scrolledJustNow(this.element)) return

      booked ??= setTimeout(
        () => this.mountForInteraction(),
        MOUNT_AFTER_HOVER_MS
      )
    })

    // A cursor crossing the canvas passes over every node on its way; only the one it stays on was
    // reached for.
    on('pointerleave', () => {
      clearTimeout(booked)
      booked = null
    })

    on('pointerdown', (event) => {
      this.mountForInteraction()
      this.placeCaretAtPointer(event)
    })

    on('focusin', (event) => {
      const reachedByKeyboard = event.target === this.interactionFocusHost
      this.mountForInteraction()
      if (reachedByKeyboard)
        this.editor?.commands.focus('start', { scrollIntoView: false })
    })
  }

  // No `autofocus`: the event that triggered the mount is what decides where the caret goes.
  // Honouring it would mean merely reaching for a node that had asked to be autofocused stole the
  // caret from wherever the user actually was. The node that does want the caret is the one just
  // inserted, and it mounts through `isLastAddedNode` in armInteractionMount with its autofocus
  // intact.
  mountForInteraction() {
    this.initializeEditor({ autofocus: false })
  }

  get interactionFocusHost() {
    return this.hasWysiwygTarget ? this.wysiwygTarget : null
  }

  // Three settings are applied by the editor rewriting the stored HTML on mount rather than by CSS
  // — the heading tag (HeadingResolver only rewrites `tag: :auto` server-side), the bullet icon
  // (the icon preset's CSS carries colour and size, not the glyph), and stripping the inline
  // text-align the toolbar wrote, which would otherwise beat the alignment setting's own rule. The
  // node whose setting the user just changed re-renders carrying the OLD content, so leaving it to
  // wait for a hover makes the change look like it did nothing.
  //
  // Read from the content rather than from a "something changed" flag, for two reasons. It is
  // self-correcting: once the rewrite is saved the content agrees again and the node goes back to
  // waiting, which is every node on a page nobody just edited. And it is cache-safe, where a flag
  // would not be — Builder::NodeRenderCache keys on the node checksum, not on the per-request
  // kwargs that carry `unset_alignment`, so a flag baked into a shared entry would mount that node
  // eagerly for the rest of the 30-minute TTL.
  contentDisagreesWithSettings() {
    // Attribute reads before the placeholder lookup, which is a Stimulus target resolution: none of
    // the three is set on a plain paragraph, and plain paragraphs are most of a page.
    if (
      !this.unsetAlignmentValue &&
      !this.headingTagValue &&
      !this.listIconValue
    )
      return false
    // An empty node has no placeholder content — only a hint, which is not content and lives in its
    // own target. So a blank line is never a node the editor owes a rewrite to, which matters: it
    // would otherwise mount on load and setHeadingTag would save `<h2></h2>` over the blank.
    if (!this.hasPlaceholderContentTarget) return false
    const content = this.placeholderContentTarget

    return (
      (this.unsetAlignmentValue &&
        !!content.querySelector('[style*="text-align"]')) ||
      (this.isAllowedHeadingTag(this.headingTagValue) &&
        content.firstElementChild?.tagName.toLowerCase() !==
          this.headingTagValue) ||
      (!!this.listIconValue &&
        Array.from(content.querySelectorAll('li[data-icon-class]')).some(
          (item) => item.dataset.iconClass !== this.listIconValue
        ))
    )
  }

  disarmInteractionMount() {
    if (!this._interactionMountAbort) return

    this._interactionMountAbort.abort()
    this._interactionMountAbort = null
    this.interactionFocusHost?.removeAttribute('tabindex')
  }

  // The press that mounted the editor hit an element ProseMirror has since replaced, so the
  // browser has nothing left to put a caret in. Map the pointer back to a document position.
  placeCaretAtPointer(event) {
    const pos = this.editor?.view?.posAtCoords({
      left: event.clientX,
      top: event.clientY,
    })
    this.editor?.commands.focus(pos ? pos.pos : 'end', {
      scrollIntoView: false,
    })
  }

  disconnect() {
    this.disarmInteractionMount()
    this.unbindPortaledToolbarActions()

    // BubbleMenu detaches the toolbar into a tippy root (usually under body), so look it up
    // via toolbarEl — querySelector on this.element alone often misses the popup. Done before
    // the early return below.
    //
    // Emptying it is what frees that DOM; removing the root only unlinks it from the document.
    // BubbleMenu holds the toolbar element in TWO places that both hang off the editor — the
    // extension's `options.element` and the plugin options captured by `spec.view` — and the
    // editor outlives its node (see the `resolveCache` note below), so dropping either
    // reference alone changes nothing, measurably. Emptying does not care how many are left:
    // what they hold is then one bare div instead of the ~3,100 nodes of the colour, palette
    // and decoration panels. Measured on a 250-element page, 3,288 retained DOM nodes an edit
    // before and 222 after. Safe because `appendToolbarEl` builds a fresh toolbar from the
    // <template> per editor — drop this line if a shared toolbar ever replaces that.
    this.toolbarEl?.closest('[data-tippy-root]')?.remove()
    this.element.querySelector('[data-tippy-root]')?.remove()
    this.toolbarEl?.replaceChildren()
    if (this.toolbarEl) delete this.toolbarEl.tiptapEditorController
    this.toolbarEl = null

    if (!this.editor) {
      this.debouncedOnUpdate?.cancel()
      return
    }

    this.removeClearContentOnFormResetEvent()

    // Run a pending debounced update now, while the editor still exists: left to fire later it
    // would read the destroyed editor and throw (typing, then closing the pane at once). Running it
    // rather than canceling it keeps the last keystrokes in the field a remount reloads from.
    // Flushing (not canceling) also keeps the edit when a breakpoint resize moves this subtree and
    // the editor remounts from the field. Cancel afterwards so nothing fires on the destroyed editor.
    this.debouncedOnUpdate?.flush?.()
    this.debouncedOnUpdate?.cancel?.()

    this.editor.destroy()
    this.editor = null

    // ToolbarActions holds its OWN reference to the editor, so nulling this.editor alone
    // still leaves the destroyed view reachable from this controller. Released for the same
    // reason as this.editor; it is hygiene rather than a leak fix.
    //
    // What retains a replaced editor is ProseMirror's own module-level ring of the last
    // twelve resolved positions (`resolveCacheSize = 12` in prosemirror-model): a cached
    // ResolvedPos reaches NodeType.spec.toDOM, whose closure holds the editor. That is
    // bounded and self-limiting — retention stops dead on the thirteenth edit — so there is
    // nothing to fix in the ring itself. What those twelve editors each drag along is a
    // different question, and is why the toolbar is emptied above. Measure at least ~15 edits
    // before reading a wysiwyg node's retention, or the fill window looks like an unbounded
    // leak.
    this.toolbarActions = null
  }

  // Force-dismiss the floating BubbleMenu toolbar. Blur alone is unreliable when preventHide /
  // preventBubbleMenuHide was set (e.g. decoration panel, mousedown on toolbar, side panel close).
  hideBubbleMenu() {
    try {
      delete window.preventBubbleMenuHide
      window.preventBubbleMenuHide = false
    } catch (_) {
      // ignore non-configurable leftovers (strict mode throws on locked props)
    }

    const tippyRoot = this.toolbarEl?.closest('[data-tippy-root]')
    tippyRoot?._tippy?.hide()
    if (this.toolbarEl) this.toolbarEl.style.visibility = 'hidden'
    this.editor?.commands.blur()
  }

  // BubbleMenu portals the toolbar outside this controller's element. Stimulus
  // then skips `->tiptap--editor#…` actions (willBeInvokedByEvent / scope
  // containsElement). Re-invoke them from listeners on toolbarEl.
  bindPortaledToolbarActions() {
    this.unbindPortaledToolbarActions()
    if (!this.toolbarEl) return
    if (this.element.contains(this.toolbarEl)) return

    this._relayToolbarAction = (event) => this.relayPortaledToolbarAction(event)
    this._portaledToolbarEvents = collectTiptapEditorEventTypes(this.toolbarEl)
    this._portaledToolbarEvents.forEach((type) => {
      this.toolbarEl.addEventListener(type, this._relayToolbarAction)
    })
  }

  unbindPortaledToolbarActions() {
    if (
      !this.toolbarEl ||
      !this._relayToolbarAction ||
      !this._portaledToolbarEvents
    )
      return
    this._portaledToolbarEvents.forEach((type) => {
      this.toolbarEl.removeEventListener(type, this._relayToolbarAction)
    })
    this._relayToolbarAction = null
    this._portaledToolbarEvents = null
  }

  relayPortaledToolbarAction(event) {
    let el = event.target
    if (!(el instanceof Element)) el = el.parentElement
    while (el && this.toolbarEl.contains(el)) {
      const method = matchingTiptapEditorMethod(
        el.getAttribute('data-action'),
        event.type
      )
      if (method && typeof this[method] === 'function') {
        const params = stimulusParamsFromElement(el, 'tiptap--editor')
        this[method](Object.assign(event, { params }))
        return
      }
      el = el.parentElement
    }
  }

  // Initialization is deferred to idle time so scrolling editors into range never blocks the frame.
  // The isVisible re-check guards against transient appearances: while the page loads, images sizing
  // in shift far-away editors briefly through the observed area. The actual construction is handed
  // to the shared lazyInitQueue rather than run here directly: on a content-heavy page, many editors
  // can become eligible in the same burst (their requestIdleCallback deadlines all land around the
  // same time), and running them all in one turn is what freezes the tab — the queue spreads them
  // across separate turns instead. ENG-33781.
  appear() {
    if (this.editor || this.cancelScheduledInitialize) return

    const [schedule, cancel] = window.requestIdleCallback
      ? [window.requestIdleCallback, window.cancelIdleCallback]
      : [(callback) => setTimeout(callback, 50), clearTimeout]
    const id = schedule(
      () => {
        this.cancelScheduledInitialize = null
        if (this.element.isConnected && this.element.hasAttribute('isVisible'))
          // Re-check both conditions inside the queued task too: on a long page the queue can take
          // a while to drain, long enough for either to change before this editor's turn comes up.
          // isConnected guards a Turbo navigation that disconnected the controller (constructing a
          // full editor on a detached element that disconnect() never got to clean up). isVisible
          // guards an editor that scrolled back out of range while queued — without it, a stale
          // offscreen editor would still spend a full construction on its turn, delaying editors the
          // user has actually scrolled to; skipping it here is cheap, and appear() reschedules it
          // (via a fresh IntersectionObserver callback) if it comes back into range later.
          lazyInitQueue.enqueue(() => {
            if (
              this.element.isConnected &&
              this.element.hasAttribute('isVisible')
            )
              this.initializeEditor()
          })
      },
      { timeout: 500 }
    )
    this.cancelScheduledInitialize = () => cancel(id)
  }

  disappear() {
    this.cancelScheduledInitialize?.()
    this.cancelScheduledInitialize = null
  }

  // For programmatic entry points (e.g. editor:setContent aimed at an editor that hasn't scrolled
  // into view yet), initialize on the spot — initialization is synchronous.
  ensureEditor() {
    if (!this.editor) this.initializeEditor()
  }

  // Autofocus helpers (modals, ajax forms) take the opacity-0 proxy input for the first visible
  // field. Send that focus on to the editor, so typing lands there and screen readers never meet the
  // aria-hidden proxy. A failed submit also focuses the proxy, to show "Please fill out this field";
  // moving focus would close that message, so that focus stays until the person types, and what
  // they type goes into the editor instead of the invisible proxy.
  inputTargetConnected(input) {
    input.addEventListener('invalid', this.proxyInvalid)
    input.addEventListener('focus', this.proxyFocused)
    input.addEventListener('beforeinput', this.proxyTyped)
  }

  inputTargetDisconnected(input) {
    input.removeEventListener('invalid', this.proxyInvalid)
    input.removeEventListener('focus', this.proxyFocused)
    input.removeEventListener('beforeinput', this.proxyTyped)
  }

  proxyTyped = (event) => {
    event.preventDefault()
    this.focusEditor()
    const text = event.data ?? event.dataTransfer?.getData('text/plain')
    if (text && this.editor) {
      const { view } = this.editor
      view.dispatch(view.state.tr.insertText(text))
    }
  }

  proxyInvalid = () => {
    this.proxyShowsValidity = true
  }

  proxyFocused = () => {
    if (this.proxyShowsValidity) {
      this.proxyShowsValidity = false
      return
    }
    this.focusEditor()
  }

  // `detail: { scrollIntoView: false }` focuses without moving the surrounding scroll
  // container — for callers focusing an editor the user didn't click at (e.g. a panel
  // opening on a tab that is scrolled elsewhere).
  focusEditor = (event) => {
    this.ensureEditor()
    this.editor?.commands.focus(event?.detail?.position ?? null, {
      scrollIntoView: event?.detail?.scrollIntoView !== false,
    })
  }

  // EDITOR INITIALIZATION
  // `autofocus: false` vetoes the autofocus *values*; it is not a caret instruction.
  initializeEditor({ autofocus = true } = {}) {
    if (this.editor) return

    this.disarmInteractionMount()

    try {
      if (this.hasPlaceholderContentTarget)
        this.placeholderContentTarget.remove()
      if (this.hasPlaceholderHintTarget) this.placeholderHintTarget.remove()
      this.appendToolbarEl()
      this.editor = this.initializeTiptap({ autofocus })
      this.toolbarActions = new ToolbarActions(this.editor)
      this.loadToolbarContent()
      this.setHeadingTag()

      if (this.unsetAlignmentValue) {
        this.unsetAlignment()
      }

      if (this.listIconValue) {
        this.performToolbarActionWithArgs({
          detail: {
            action: 'changeBulletIcon',
            args: { faClasses: this.listIconValue },
            options: { focus: false },
          },
        })
      }

      this.addClearContentOnFormResetEvent()

      const localStorageDraftKey = this.element.dataset.localDraftStorageKey
      if (localStorageDraftKey) {
        const $editor = $(this.element)
        $editor.setupManager(LocalDraftManager, 'LocalDraftManager')
        this.draftManager = $editor.data('simpleroManagerLocalDraftManager')
        // Settles once a saved draft has (or hasn't) been restored, for callers
        // that insert content and must not race the restore.
        this.draftRestore = this.draftManager.onTiptapLoaded(this.editor)
      }

      // Dispatch custom event for tracking
      this.element.dispatchEvent(
        new CustomEvent('tiptap:initialized', {
          bubbles: true,
          detail: { controller: this },
        })
      )
    } catch (error) {
      console.error('Failed to initialize TipTap editor:', error)
      // Could implement retry logic here
    }
  }

  // We keep the toolbar inside a template tag and append always on connect - so if the editor is removed and re-added
  // on the DOM we don't have a missing toolbar
  appendToolbarEl() {
    if (!this.hasToolbarTemplateTarget) return

    this.element.insertAdjacentHTML(
      'beforeend',
      this.toolbarTemplateTarget.innerHTML
    )
    // BubbleMenu detaches the toolbar element from the DOM right after this, so grab a reference
    // while the target lookup still works
    this.toolbarEl = this.hasToolbarTarget ? this.toolbarTarget : null
    // Reverse of toolbarControl: BubbleMenu portals the toolbar out of this.element, so controls
    // inside it (the decoration panel) can't find their editor through DOM ancestry.
    if (this.toolbarEl) this.toolbarEl.tiptapEditorController = this
    this.toolbarContentLoading = false
    // We have tooltips on the toolbar buttons, the JS for that is in global_behaviors.js and is triggered on
    // window.onDomChanged, but as the toolbar is dynamically added to the DOM we need to trigger it manually
    $('.tiptap-toolbar-btn').changed()
    $('.js-tiptap-color-picker-btn').changed()
  }

  // The toolbar shell only holds a spinner; the actual controls (ToolbarContentComponent) are fetched
  // from toolbarContentUrlValue when the editor initializes, so they're usually ready before the
  // toolbar first appears. Since BubbleMenu keeps the toolbar detached from the DOM until it's first
  // shown, no Stimulus controller inside it could drive this — the editor has to own the fetch.
  // Editors with the same configuration share identical URLs, so the request is cached and made only
  // once per URL for the whole page.
  static toolbarContentRequests = new Map()

  loadToolbarContent() {
    const placeholder = this.toolbarEl?.querySelector(
      '.js-tiptap-toolbar-options'
    )
    if (
      !placeholder ||
      !this.hasToolbarContentUrlValue ||
      this.toolbarContentLoading
    )
      return
    this.toolbarContentLoading = true

    const url = this.toolbarContentUrlValue
    const requests = this.constructor.toolbarContentRequests
    if (!requests.has(url)) {
      requests.set(
        url,
        $.ajax({ url, method: 'GET', dataType: 'html', global: false })
      )
    }

    requests
      .get(url)
      .done((html) => {
        const $content = $(html).filter('.js-tiptap-toolbar-options')
        $(placeholder).replaceWith($content)
        $content.changed()
        // Only sync if this toolbar is actually on screen. The content fetch runs for
        // EVERY editor as it initializes, and syncing here cost a getComputedStyle per
        // editor for a toolbar nobody was looking at. BubbleMenu's `onShown` calls
        // syncToolbarState when a toolbar is displayed, so a skipped sync here is picked
        // up the moment the user opens it.
        if (this.toolbarNeedsSync) this.syncToolbarState()
        this.bindPortaledToolbarActions()
      })
      .fail(() => {
        requests.delete(url)
        this.toolbarContentLoading = false
      })
  }

  // Every editor fires onTransaction when it is created, and a large builder document
  // holds hundreds of them (267 on a 673-node page). Syncing all of them ran a
  // querySelectorAll per editor plus a getComputedStyle through fetchDefaultFontSize --
  // a forced style recalc -- to update a toolbar that only one editor ever shows. That
  // was ~5s of a 21.7s editor load and ~2s of a section edit. fetchDefaultFontFamily
  // reads the same computed style, so this same guard covers it.
  //
  // BubbleMenu keeps the toolbar detached from the DOM until it is first shown (see
  // loadToolbarContent), so an unconnected toolbarEl means this editor's toolbar has
  // never been displayed. Err toward syncing: a focused editor always qualifies, and
  // so does a toolbar that is in the document and not hidden -- clicking a toolbar
  // button can momentarily drop editor focus.
  get toolbarNeedsSync() {
    if (this.editor?.isFocused) return true

    const el = this.toolbarEl

    return !!el && el.isConnected && el.style.visibility !== 'hidden'
  }

  // A control in this editor's toolbar, e.g. toolbarControl('fontSizeInput'). Not a Stimulus
  // target: BubbleMenu's appendTo moves the toolbar into .tippy-portal, outside this.element, and
  // Stimulus only finds targets inside it -- so as targets, every toolbar sync silently did
  // nothing (ENG-26884). The markup still tags them `data-tiptap--editor-target`.
  toolbarControl(name) {
    return (
      this.toolbarEl?.querySelector(
        `[data-tiptap--editor-target~="${name}"]`
      ) ?? null
    )
  }

  // Called from BubbleMenu's `onShown` (extensions.js) and from the toolbar-content
  // fetch. Unconditional by design: its callers decide whether a sync is warranted.
  syncToolbarState = () => {
    if (!this.editor) return

    updateToolbarButtonsActiveState(this.editor, this)
    this.fireSetTextColorEvent()
    this.fireSetHighlightColorEvent()
    this.fireSetDecorationFromEditorEvent()
    try {
      this.toolbarEl?.closest('[data-tippy-root]')?._tippy?.setProps({})
    } catch {
      // the toolbar's tippy instance is already torn down — nothing to reposition
    }
  }

  initializeTiptap = ({ autofocus }) => {
    const self = this

    let content = this.inputTarget.value
    if (this.markdownValue) {
      content = marked.parse(this.inputTarget.value)
    }

    this.debouncedOnUpdate = debounce(this.onUpdate, this.debounceValue)

    const options = {
      element: this.wysiwygTarget,
      extensions: this.extensions(),
      content,
      onUpdate: this.debouncedOnUpdate,
      onTransaction: debounce(({ editor, _transaction }) => {
        if (!this.toolbarNeedsSync) return

        updateToolbarButtonsActiveState(editor, this)
      }, 5),
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      injectCSS: false,
      editorProps: {
        attributes: {
          role: 'textbox',
          'aria-multiline': 'true',
          'aria-label': this.wysiwygTarget.dataset.accessibleName,
          'aria-required': String(
            this.hasInputTarget && this.inputTarget.required
          ),
        },
        transformPastedHTML(html) {
          return self.removeInlineStyles(html)
        },
        handlePaste(view, event, _slice) {
          const rawText = event.clipboardData.getData('text/plain')
          const pastedText = rawText.trim()
          const embedTarget = self.embedTargetFor(pastedText)
          const urlAction = pastedUrlAction({
            text: pastedText,
            selectionEmpty: view.state.selection.empty,
            embeddable: !!embedTarget,
            attached:
              !!embedTarget && !!embedAttachState(pastedText, embedTarget.form),
          })

          // A bare URL pasted over a selection: don't treat it as Markdown to
          // insert — fall through so @tiptap/extension-link's own paste handler
          // turns the selection into a link instead of replacing it.
          if (urlAction === 'link-selection') return false

          if (urlAction === 'insert-link') {
            self.insertLink(pastedText)
            return true
          }

          // Keep the URL in the text as well as attaching it, so the post
          // still reads right without the embed.
          if (urlAction === 'embed') {
            self.insertLink(pastedText)
            self.startEmbed(pastedText, embedTarget)
            return true
          }

          // Plain-text-only paste that looks like Markdown → convert to rich text.
          // Applies to every editor (not just Markdown-storage ones): pasting from
          // AI tools, docs, or .md files should render. Rich/web copy carries
          // text/html and goes through transformPastedHTML instead.
          if (pastedText && !event.clipboardData.types.includes('text/html')) {
            self.editor.commands.insertContent(markdownToHtml(rawText))
            return true
          }

          const files = event.clipboardData.files
          const dropzoneEl = self.element
            .closest('form.js-dropzone-form')
            ?.querySelector('.js-dropzone-container')

          if (dropzoneEl) {
            dropzoneEl.dispatchEvent(
              new CustomEvent('dropzone:addFiles', {
                detail: {
                  files: files,
                },
              })
            )
          }
          return false
        },
      },
    }

    if (
      autofocus &&
      (isLastAddedNode(this.element) ||
        (this.hasAutofocusValue && this.autofocusValue))
    ) {
      options.autofocus = 'end'
    }

    return new Editor(options)
  }

  removeInlineStyles = (html) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')

    // Select all elements
    const allElements = doc.querySelectorAll('*')

    // Loop through each element and remove the style attribute
    allElements.forEach((element) => {
      element.removeAttribute('style')
    })

    // Serialize the Document object back to an HTML string
    return doc.documentElement.outerHTML
  }

  onUpdate = () => {
    const html = this.editor.getHTML()
    // If they typed in something and removed it don't set empty p tag on the text field
    // Helps in validating the text field with required attribute
    let value = html === '<p></p>' ? '' : html

    if (this.markdownValue) {
      if (!window.TURNDOWN_SERVICE) {
        const service = new TurndownService({
          headingStyle: 'atx',
          // Preserve empty paragraphs as HTML so extra blank lines survive the roundtrip.
          // TurndownService routes blank nodes here *before* checking custom rules.
          // Without this, empty <p> becomes \n\n which marked.parse() collapses.
          blankReplacement: (content, node) => {
            if (node.nodeName === 'P') return '\n\n<p></p>\n\n'
            return '\n\n'
          },
        })

        // Preserve underlines as HTML (standard Markdown has no underline syntax)
        service.addRule('underline', {
          filter: ['u'],
          replacement: (content) => `<u>${content}</u>`,
        })

        addBotObjectRule(service)
        addTightListRules(service)

        // GFM: strikethrough (~~), GFM task lists, and — critically — tables.
        // Without this, TipTap's <table>…</table> HTML turns into a jumble of
        // bare cell contents on separate lines when saved back to markdown.
        gfm(service)

        window.TURNDOWN_SERVICE = service
      }
      value = window.TURNDOWN_SERVICE.turndown(html)
    }

    this.inputTarget.value = value
    this.inputTarget.dispatchEvent(
      new Event('wysiwyg-changed', { bubbles: true })
    )

    // Reuse the HTML we already serialized above instead of making the draft
    // manager call editor.getHTML() again on every keystroke (ENG-32364).
    this.draftManager?.onChange(html)
  }

  onFocus = () => {
    this.element.dispatchEvent(
      new CustomEvent('tiptap:focus', { bubbles: true })
    )
  }

  onBlur = () => {
    // Leaving the editor must not strand the last keystrokes behind the update debounce: a
    // click on Publish blurs the editor first, and would otherwise publish before the edit
    // is even sent.
    this.debouncedOnUpdate?.flush()
    this.element.dispatchEvent(
      new CustomEvent('tiptap:blur', { bubbles: true })
    )
  }

  // Embeddable URLs (YouTube, Vimeo, Loom, Rumble, Spotify) on a form with video
  // attachments become an Embed attachment — same as the manual "Add video embed"
  // button. Returns the form and object name to embed into, or null.
  embedTargetFor = (text) => {
    if (!isBareUrl(text)) return null
    if (!EMBEDDABLE_URL_PATTERNS.some((pattern) => pattern.test(text)))
      return null

    const form = this.element.closest('form.js-dropzone-form')
    if (!form) return null

    const embedComponent = form.querySelector(
      '[data-community--attachments--new-video-embed-object-name-value]'
    )
    if (!embedComponent) return null

    const objectName =
      embedComponent.dataset[
        'community--attachments--newVideoEmbedObjectNameValue'
      ]

    return { form, objectName }
  }

  createEmbedFromUrl = (text) => {
    const embedTarget = this.embedTargetFor(text)
    if (!embedTarget) return false

    this.startEmbed(text, embedTarget)
    return true
  }

  // A failed embed falls back to the URL as text — unless it's already in as a
  // link (a paste always inserts one).
  startEmbed = (text, { form, objectName }) => {
    createEmbed(text, objectName, form).catch((error) => {
      window.showNotice('alert', error.message || String(error))
      if (!this.hasLinkTo(text)) this.editor.commands.insertContent(text)
    })
  }

  hasLinkTo = (url) => {
    let found = false
    this.editor.state.doc.descendants((node) => {
      found ||= node.marks.some(
        (mark) => mark.type.name === 'link' && mark.attrs.href === url
      )
      return !found
    })
    return found
  }

  insertLink = (url) => {
    this.editor.commands.insertContent({
      type: 'text',
      marks: [{ type: 'link', attrs: { href: url } }],
      text: url,
    })
  }

  performToolbarAction = (event) => {
    this.toolbarActions[event.params.action]()
  }

  // function to communicate with other stimulus controllers, just dispatch the custom event with keys: action and args inside detail
  // see image_modal_component_controller.js for example
  performToolbarActionWithArgs = (event) => {
    this.toolbarActions[event.detail.action](
      event.detail.args,
      event.detail.options
    )
  }

  extensions = () => {
    return configuredExtensions(this)
  }

  setHighlightColor = (e) => {
    let event = {}
    event.detail = {
      action: 'setHighlight',
      args: { color: e.detail.color },
    }
    this.performToolbarActionWithArgs(event)
  }

  setDecoration = (e) => {
    const { focus, ...args } = e.detail
    this.performToolbarActionWithArgs({
      detail: {
        options: { focus },
        action: 'setDecoration',
        args,
      },
    })
  }

  unsetDecoration = () => {
    this.performToolbarAction({ params: { action: 'unsetDecoration' } })
  }

  fireSetDecorationFromEditorEvent = () => {
    fireSetDecorationFromEditorEvent(this)
  }

  setTextColor = (e) => {
    let event = {}
    event.detail = {
      action: 'setTextColor',
      args: { color: e.detail.color },
    }
    this.performToolbarActionWithArgs(event)
  }

  fireSetHighlightColorEvent = () => {
    fireSetHighlightColorEvent(this)
  }

  insertFaIcon = (e) => {
    let event = {}
    event.detail = {
      action: 'insertFaIcon',
      args: { faClasses: e.detail.faClasses },
    }
    this.performToolbarActionWithArgs(event)
  }

  fireSetTextColorEvent = () => {
    fireSetTextColorEvent(this)
  }

  resetHighlightColor = () => {
    this.performToolbarAction({ params: { action: 'unsetHighlight' } })
    this.fireSetHighlightColorEvent()
  }

  resetTextColor = () => {
    this.performToolbarAction({ params: { action: 'unsetColor' } })
    this.fireSetTextColorEvent()
  }

  // ⌘K / Ctrl+K. Returns whether the editor took the key: an editor without a link control
  // leaves it to the page (the community search shortcut).
  linkShortcut = () => {
    if (!this.toolbarEl) return false

    if (this.linkPickerValue) {
      this.openLinkPicker(true)
    } else {
      // Missing only while the toolbar controls are still loading.
      const basicLinkButton = this.toolbarControl('basicLinkButton')
      if (basicLinkButton) this.openBasicLink(basicLinkButton)
    }
    return true
  }

  // Edit from the link info box. Clicking the box took focus from the editor, and the
  // BubbleMenu (with the link form) only shows on a focused editor.
  editLink() {
    this.editor.chain().focus().extendMarkRange('link').run()
    this.linkShortcut()
  }

  // The site toolbar's link form is a dropdown anchored to its button in the BubbleMenu, so it
  // needs a selection: with just a caret, select the link it is in, or else the word.
  openBasicLink(button) {
    if (this.editor.state.selection.empty) {
      if (!this.selectLinkAtCaret() && !this.selectWordAtCaret()) return
    }

    // A selection made just now shows the BubbleMenu only after its update delay (250ms), so
    // wait for it, up to a second.
    const open = (triesLeft = 20) => {
      const shown =
        this.toolbarEl?.isConnected &&
        this.toolbarEl.style.visibility === 'visible'
      if (!shown) {
        if (triesLeft > 0) setTimeout(() => open(triesLeft - 1), 50)
        return
      }
      button.click()
    }
    open()
  }

  // The whole link, also with the caret at either edge of it: the link mark is not inclusive, so
  // isActive('link') misses the edges, and the word there is only part of a longer link.
  selectLinkAtCaret() {
    const { state } = this.editor
    const { $from } = state.selection
    const type = state.schema.marks.link
    const range =
      getMarkRange($from, type) ||
      (type.isInSet($from.nodeAfter?.marks ?? []) &&
        getMarkRange(state.doc.resolve($from.pos + 1), type))
    if (!range) return false

    this.editor.commands.setTextSelection(range)
    return true
  }

  selectWordAtCaret() {
    const { $from } = this.editor.state.selection
    const block = $from.parent
    // One character per inline leaf (mention, image), so string offsets match positions.
    const text = block.textBetween(0, block.content.size, null, '\ufffc')
    const words = new Intl.Segmenter(undefined, {
      granularity: 'word',
    }).segment(text)
    const offset = $from.parentOffset
    // The caret right after a word still counts as in it.
    let word = words.containing(offset)
    if (!word?.isWordLike && offset > 0) word = words.containing(offset - 1)
    if (!word?.isWordLike) return false

    const start = $from.start() + word.index
    this.editor.commands.setTextSelection({
      from: start,
      to: start + word.segment.length,
    })
    return true
  }

  openLinkPicker = (onlyText) => {
    this.dispatch('openLinkPicker', {
      detail: {
        action: 'openLinkPicker',
        args: {
          editorController: this,
          onlyText: onlyText,
        },
      },
    })
  }

  clearContent = () => {
    this.ensureEditor()
    this.editor.commands.clearContent()
  }

  get form() {
    return this.editor.view.dom.closest('form')
  }

  addClearContentOnFormResetEvent = () => {
    if (this.form) {
      this.form.addEventListener('reset', this.clearContent)
      this.form.addEventListener('submit', this._syncSourceBeforeSubmit, true)
    }
  }

  removeClearContentOnFormResetEvent = () => {
    if (this.form) {
      this.form.removeEventListener('reset', this.clearContent)
      this.form.removeEventListener(
        'submit',
        this._syncSourceBeforeSubmit,
        true
      )
    }
  }

  // Writes any pending debounced edit to the input now, for callers that serialize the form
  // right away (e.g. closing the source edit panel).
  flush = () => {
    this.debouncedOnUpdate?.flush()
  }

  // Runs in the capture phase, so before the form is serialized. Flushing writes an edit made
  // inside the debounce window to the hidden input; Mod-Enter reaches here via the submit
  // button click.
  _syncSourceBeforeSubmit = () => {
    this.debouncedOnUpdate?.flush?.()
    if (this._sourceMode && this.hasSourceTarget) {
      this.editor.commands.setContent(this.sourceTarget.value)
      this.onUpdate()
    }
  }

  // content - pass an array of single/multiple objects for example:
  // {
  //   type: 'paragraph',
  //   content: [
  //      {
  //        type: 'text',
  //        text: 'Second paragraph',
  //      },
  //   ],
  // },
  // Pass `markdown: true` when `content` is a Markdown string rather than HTML.
  setContent = (event) => {
    this.ensureEditor()
    applySetContent(this.editor, event.detail)
    if (event.detail.immediate) this.onUpdate()
  }

  // Pass `focus: false` to append without focusing (and so without raising a
  // mobile keyboard the user didn't ask for).
  appendText = (event) => {
    const text = event.detail?.text
    if (!text) return
    this.ensureEditor()
    if (event.detail?.focus === false) {
      // Replace an empty editor's placeholder paragraph rather than appending
      // after it, which would leave a blank first line.
      const size = this.editor.state.doc.content.size
      const range = this.editor.isEmpty ? { from: 0, to: size } : size
      this.editor.commands.insertContentAt(range, text)
    } else this.editor.chain().focus('end').insertContent(text).run()
    if (event.detail?.immediate) this.onUpdate()
  }

  // Like appendText, but the incoming text is Markdown — convert it to rich content
  // first so lists, paragraphs, etc. render as nodes instead of literal "- " text.
  // Used by voice dictation, whose transcript comes back Fluid-Voice-formatted.
  // Pass `focus: false` to update the form value without focusing the editor
  // (record→send silent auto-submit hides the composer and shouldn't steal focus).
  // Always appends at the document end — never at the current caret — so a mid-word
  // selection doesn't splice the transcript into the middle of existing text.
  appendMarkdown = (event) => {
    const text = event.detail?.text
    if (!text) return
    this.ensureEditor()
    const endPos = this.editor.state.doc.content.size
    const chain = this.editor
      .chain()
      .insertContentAt(endPos, markdownToHtmlOrText(text))
    if (event.detail?.focus !== false) chain.focus('end')
    chain.run()
    if (event.detail?.immediate) this.onUpdate()
  }

  setInputValue = () => {
    this.dispatch('setInputValue', {
      detail: {
        action: 'setInputValue',
        args: {
          editorController: this,
        },
      },
    })
  }

  split = (event) => {
    const { element } = event.params
    const { state } = this.editor
    const { from, to } = state.selection

    // Get the list of top-level nodes in the doc
    const nodes = state.doc.content.content

    // Find the selected nodes. May be one node or many if selection spans multiple lines.
    const fromResolvedPos = state.doc.resolve(from)
    const toResolvedPos = state.doc.resolve(to)

    const startLine = nodes.indexOf(fromResolvedPos.parent)
    const endLine = nodes.indexOf(toResolvedPos.parent)

    this.inputTarget.dispatchEvent(
      new CustomEvent('wysiwyg-split', {
        bubbles: true,
        detail: { startLine, endLine, element },
      })
    )
  }

  getCurrentAlignment = () => {
    return ['left', 'center', 'right', 'justify'].find((alignment) =>
      this.editor.isActive({ textAlign: alignment })
    )
  }

  // Used in Builder Doc headline, subheadline, preheadline elements to set the tag from sidebar
  setHeadingTag = () => {
    const tag = this.headingTagValue
    if (!tag || !this.isAllowedHeadingTag(tag)) return

    const level = this.extractHeadingLevel(tag)
    if (this.isCurrentHeadingLevel(level)) return

    // This should come before the tag is changed.
    // Reason is that changing the tag may change the alignment. We want to know what the original
    // alignment was before that so we can apply it again afterwards.
    const alignment = this.getCurrentAlignment()

    // `focus: false` because ToolbarActions' chain otherwise starts with `focus()`, and TipTap's
    // focus scrolls the node into view — this runs while the editor is being built, before the user
    // has asked for anything, so on a long page it would jump the canvas to whichever node happened
    // to disagree with its tag setting. Same escape hatch the bullet icon uses on mount.
    this.toolbarActions[tag](null, { focus: false })

    // Keep the current alignment (if any)
    if (alignment) {
      this.editor.commands.setTextAlign(alignment)
    }
  }

  isAllowedHeadingTag = (tag) => {
    const allowedTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
    return allowedTags.includes(tag)
  }

  extractHeadingLevel = (tag) => {
    return parseInt(tag.split('h')[1])
  }

  isCurrentHeadingLevel = (level) => {
    const currentLevel = this.editor.getAttributes('heading').level
    return level === currentLevel
  }

  addPersonalization = (event) => {
    const { key, label } = event.params
    const snippet = promptedPersonalizationSnippet(key, { label })
    if (!snippet) return

    if (snippet.type === 'action') {
      this.runPersonalizationAction(snippet.action)
    } else {
      this.editor.commands.insertContent(snippet.content)
    }
  }

  runPersonalizationAction = (action) => {
    if (action === 'countdownTimer') {
      // Tiptap parity for the countdown-timer modal is tracked as a follow-up
      // (see plan "Known gaps"). For now, do nothing rather than insert a broken tag.
      return
    }
  }

  setFontFamily = (e) => {
    const value = e.detail?.value
    if (!value) return

    // The picker's `value` is an identifier (e.g. "simplero-custom-42" for custom fonts) that
    // doesn't match any registered @font-face name. Resolve it to the actual font-family CSS
    // string so the inline `font-family:` lines up with the @font-face declared by CustomFont.
    const option = window.FONT_FAMILY_OPTIONS_FOR_FONT_SELECT_COMPONENT?.find(
      (font) => font.value == value
    )
    const fontFamily = option?.extraData?.cssString || value

    // The picked font's stylesheet is not in this document yet -- see loadWebfont. `ownerDocument`
    // because on a builder canvas the editor lives in the preview iframe, not the shell around it.
    loadWebfont(option, this.element.ownerDocument)

    let event = {}
    event.detail = {
      action: 'setFontFamily',
      args: fontFamily,
    }
    this.performToolbarActionWithArgs(event)
  }

  setFontSize = (e) => {
    const value = e.detail?.value
    if (!value) return

    const unit = this.getFontSizeUnit()
    const fontSizeWithUnit = value + unit

    // Directly set font size on editor without dispatching events (prevents focus loss)
    this.editor.commands.setFontSize(fontSizeWithUnit)
  }

  setFontSizeUnit = (e) => {
    const newUnit = e.detail.value
    if (!newUnit) return

    // With no inline size (e.g. a style-guide size), convert the size the toolbar shows: the one the
    // text renders at, in px. Otherwise the unit changes and the number doesn't (ENG-26884).
    const currentFontSize =
      this.editor.getAttributes('textStyle').fontSize ||
      fetchDefaultFontSize(this)
    if (!currentFontSize) return

    const match = currentFontSize.toString().match(/^([\d.]+)\s*([a-z%]*)$/i)
    if (!match) return

    const currentValue = parseFloat(match[1])
    const currentUnit = (match[2] || 'px').toLowerCase()

    const pxValue = this.convertToPx(currentValue, currentUnit)
    const convertedValue = this.convertFromPx(pxValue, newUnit)

    const fontSizeInput = this.toolbarControl('fontSizeInput')
    if (fontSizeInput) fontSizeInput.value = convertedValue
    this.editor.commands.setFontSize(convertedValue + newUnit)

    // Resize input to match new value width
    this.dispatchInputResize()
  }

  convertToPx(value, unit) {
    if (unit === 'px') return value

    // Get the actual computed font sizes for accurate conversion
    const parentFontSize = this.getParentFontSizePx()
    const rootFontSize = this.getRootFontSizePx()

    if (unit === 'em') return value * parentFontSize
    if (unit === 'rem') return value * rootFontSize
    if (unit === '%') return (value / 100) * parentFontSize
    return value
  }

  convertFromPx(pxValue, unit) {
    // Get the actual computed font sizes for accurate conversion
    const parentFontSize = this.getParentFontSizePx()
    const rootFontSize = this.getRootFontSizePx()

    const conversions = {
      px: (v) => Math.round(v), // Round px to whole numbers
      em: (v) => v / parentFontSize, // Keep decimals for em
      rem: (v) => v / rootFontSize, // Keep decimals for rem
      '%': (v) => Math.round((v / parentFontSize) * 100), // Round % to whole numbers
    }
    return conversions[unit]?.(pxValue) ?? pxValue
  }

  getParentFontSizePx() {
    // Get computed font size from the wysiwyg element (where the text lives)
    if (this.hasWysiwygTarget) {
      const computedStyle = window.getComputedStyle(this.wysiwygTarget)
      return parseFloat(computedStyle.fontSize) || 16
    }
    return 16 // Fallback
  }

  getRootFontSizePx() {
    // Get computed font size from the root html element
    const computedStyle = window.getComputedStyle(document.documentElement)
    return parseFloat(computedStyle.fontSize) || 16
  }

  dispatchInputResize() {
    // Trigger input event to notify input_with_unit controller to resize
    this.toolbarControl('fontSizeInput')?.dispatchEvent(
      new Event('input', { bubbles: true })
    )
  }

  getFontSizeValue = () => {
    return this.toolbarControl('fontSizeInput')?.value ?? null
  }

  getFontSizeUnit = () => {
    return this.toolbarControl('fontSizeUnitSelect')?.value ?? 'px'
  }

  unsetAlignment() {
    this.editor.commands.selectAll()
    this.editor.commands.unsetTextAlign()
    this.editor.commands.setTextSelection({ from: 0, to: 0 })
  }

  // HTML SOURCE EDITING

  toggleSource = () => {
    if (!this.hasSourceTarget) return

    this._sourceMode = !this._sourceMode

    if (this._sourceMode) {
      this.sourceTarget.value = this.editor.getHTML()
      this.wysiwygTarget.classList.add('hidden')
      this.sourceTarget.classList.remove('hidden')
      this.sourceTarget.focus()
    } else {
      this.editor.commands.setContent(this.sourceTarget.value)
      this.sourceTarget.classList.add('hidden')
      this.wysiwygTarget.classList.remove('hidden')
      this.onUpdate()
      this.editor.commands.focus()
    }
  }

  // MEDIA LIBRARY

  // In builder 2.0 the editor runs inside the preview iframe, where the assets
  // dialog lives on the top window, not this frame. Resolve from the current
  // frame first, then fall back to window.top (matching TinyMCE/ProseMirror).
  resolveAssetsDialog() {
    try {
      return (
        window.Simplero?.AssetsDialog ||
        window.top?.Simplero?.AssetsDialog ||
        null
      )
    } catch (_) {
      return window.Simplero?.AssetsDialog || null
    }
  }

  openMediaLibrary = () => {
    const dialog = this.resolveAssetsDialog()
    if (!dialog) return
    const image = this.editor.isActive('image')
      ? this.editor.getAttributes('image')
      : {}

    dialog.open({
      mode: 'html',
      media_type: this.mediaTypeValue || 'image',
      asset_id: image.assetId || assetIdFromUrl(image.src),
      display_width: image.width,
      alt: image.alt,
      allow_align: false, // The Tiptap image node has no alignment yet.
      callback: (_asset, html) => {
        if (html) {
          this.editor.commands.insertContent(html)
        }
      },
    })
  }

  // VIDEO EMBED DIALOG

  openVideoDialog = () => {
    const url = prompt('Paste a URL (YouTube, Vimeo, Loom, Rumble, Spotify):')
    if (!url) return

    if (this.createEmbedFromUrl(url)) return

    // Validate scheme before inserting as a link
    let safeUrl = url
    try {
      safeUrl = new URL(url).href
    } catch (_) {
      /* keep as-is */
    }
    if (!/^https?:\/\//i.test(safeUrl)) return

    this.insertLink(safeUrl)
  }

  // TABLE INSERT

  insertTable = () => {
    this.toolbarActions.insertTable()
  }

  // These functions will be helpful in future when we bring the functionality back:
  // Just have a single button for alignment, and it toggles the icons

  // toggleAlignment = (e) => {
  //   const button =
  //     e.target.tagName === 'BUTTON' ? e.target : e.target.closest('button')

  //   const alignments = [
  //     {
  //       check: { textAlign: 'left' },
  //       hideIcon: '.js-left',
  //       showIcon: '.js-center',
  //       action: 'alignCenter',
  //     },
  //     {
  //       check: { textAlign: 'center' },
  //       hideIcon: '.js-center',
  //       showIcon: '.js-right',
  //       action: 'alignRight',
  //     },
  //     {
  //       check: { textAlign: 'right' },
  //       hideIcon: '.js-right',
  //       showIcon: '.js-justify',
  //       action: 'alignJustify',
  //     },
  //   ]

  //   for (let { check, hideIcon, showIcon, action } of alignments) {
  //     if (this.checkIfAlignmentIsActive(check)) {
  //       this.toggleAlignmentButtonIcon(hideIcon, showIcon, button)
  //       this.toolbarActions[action]()
  //       return
  //     }
  //   }

  //   this.toggleAlignmentButtonIcon('.js-justify', '.js-left', button)
  //   this.toolbarActions.alignLeft()
  // }

  // checkIfAlignmentIsActive = (alignment) => {
  //   return this.editor.isActive(alignment)
  // }

  // toggleAlignmentButtonIcon = (hideIcon, showIcon, button) => {
  //   button.querySelector(hideIcon).classList.add('hidden')
  //   button.querySelector(showIcon).classList.remove('hidden')
  // }
}
